import React from "react";
import {
  SkeletonBlock,
  SkeletonText,
  SkeletonImage,
  SkeletonBadgeRow,
  SkeletonCircle,
} from "component/Loading";
import { useTranslation } from "react-i18next";

const SHOW_SOCIAL_BLOCK = false;

export const TopNewsSkeleton = () => (
  <div className="latest_news_card shadow-sm skeleton-card">
    <div className="latest_news_image_box position-relative">
      <SkeletonImage height="260px" radius={20} />
    </div>
    <div className="latest_news_card_overlay">
      <SkeletonText lines={2} lineHeight={22} gap={12} widths={["80%", "60%"]} />
      <SkeletonText lines={3} lineHeight={14} gap={10} widths={["100%", "95%", "70%"]} />
      <div className="news_detail d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <div className="d-flex gap-3 align-items-center small flex-wrap">
          <SkeletonBlock width="120px" height={16} />
          <SkeletonBadgeRow count={2} width="90px" />
        </div>
        <div className="d-flex align-items-center gap-3 small">
          <SkeletonBlock width="60px" height={14} />
          <SkeletonCircle size={36} />
        </div>
      </div>
    </div>
  </div>
);

export const NewsCardSkeleton = ({ viewMode = "list" }) => {
  const isList = viewMode === "list";
  const imageHeight = isList ? "223px" : "190px";

  return (
    <div
      className="news-card overflow-hidden rounded-4 skeleton-card"
      style={{ height: isList ? "223px" : "auto" }}
    >
      <div className="row g-0">
        <div
          className={`${isList ? "col-4" : "col-12"} news_image_box`}
          style={{ minHeight: imageHeight }}
        >
          <SkeletonImage height={imageHeight} />
        </div>
        <div className={`${isList ? "col-8" : "col-12"} card_detail_box d-flex flex-column justify-content-between`}>
          <div>
            <SkeletonText lines={2} lineHeight={18} gap={10} widths={["85%", "55%"]} />
            <SkeletonText lines={3} lineHeight={14} gap={10} widths={["100%", "95%", "70%"]} />
          </div>
          <div className="news_detail d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <div className="d-flex gap-2 align-items-center small flex-wrap">
              <SkeletonBlock width="120px" height={14} />
              <SkeletonBadgeRow count={2} width="80px" />
            </div>
            <div className="d-flex align-items-center gap-3 small">
              <SkeletonBlock width="50px" height={14} />
              <SkeletonCircle size={32} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const HotNewsSkeleton = () => (
  <div className="news_comment_card rounded shadow-sm skeleton-card">
    <SkeletonText lines={2} lineHeight={18} gap={12} widths={["90%", "65%"]} />
    <SkeletonText lines={2} lineHeight={12} gap={8} widths={["100%", "80%"]} />
    <div className="hot_news_detail d-flex justify-content-between align-items-center mt-3 flex-wrap gap-3">
      <div className="news_sourace_block d-flex align-items-center gap-2">
        <div className="source-avatars d-flex gap-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <SkeletonCircle key={idx} size={28} />
          ))}
        </div>
        <SkeletonBlock width="40px" height={14} />
      </div>
      {SHOW_SOCIAL_BLOCK && (
        <div className="news_socialMedia_block d-flex align-items-center gap-2">
          <SkeletonCircle size={20} />
          <SkeletonCircle size={20} />
          <SkeletonCircle size={20} />
          <SkeletonBlock width="50px" height={14} />
        </div>
      )}
      <div className="d-flex align-items-center gap-2 px-2 py-1 rounded">
        <SkeletonBlock width="60px" height={14} />
        <SkeletonCircle size={18} />
      </div>
    </div>
  </div>
);

export const AllNewsSkeleton = () => (
  <div className="news_comment_card skeleton-card">
    <SkeletonText lines={2} lineHeight={16} gap={10} widths={["85%", "60%"]} />
    <SkeletonText lines={3} lineHeight={12} gap={8} widths={["100%", "96%", "70%"]} />
    <div className="news_detail d-flex align-items-center justify-content-between gap-2 flex-wrap mt-2">
      <div className="d-flex gap-2 align-items-center small flex-wrap">
        <SkeletonBlock width="120px" height={14} />
        <SkeletonBadgeRow count={2} width="70px" />
      </div>
      <div className="d-flex align-items-center gap-3 small">
        <SkeletonBlock width="50px" height={14} />
        <SkeletonCircle size={28} />
      </div>
    </div>
  </div>
);

export const ArticleSkeleton = ({ showSidebar = true }) => {
  const { t } = useTranslation();
  return (
    <div className="container-fluid px-0">
      <div className="row g-4">
        <div className="col-12 col-xl-8" style={{ maxWidth: "950px", flex: "0 1 950px" }}>
          <div className="main_contennt_box">
            <div className="news_views_banner_image mb-3">
              <SkeletonImage height="320px" radius={20} />
            </div>
            <div className="action_box">
              <span className="fw-light title_box d-block mb-2">{t("news.quickAction")}</span>
              <div className="d-flex gap-3 flex-wrap">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <SkeletonBlock
                    key={idx}
                    height={42}
                    width="110px"
                    className="skeleton-pill"
                    style={{ borderRadius: 10 }}
                  />
                ))}
              </div>
            </div>
            <div className="content_box pt-4" style={{ paddingLeft: "50px", paddingRight: "50px" }}>
              <SkeletonText lines={2} lineHeight={26} gap={12} widths={["90%", "60%"]} />
              <SkeletonText
                lines={3}
                lineHeight={14}
                gap={10}
                widths={["100%", "96%", "70%"]}
                className="mt-3"
              />
              <SkeletonText
                lines={6}
                lineHeight={14}
                gap={10}
                widths={["100%", "98%", "92%", "90%", "95%", "60%"]}
                className="mt-4"
              />
              <div className="mt-5 d-flex flex-column gap-3">
                {Array.from({ length: 2 }).map((_, idx) => (
                  <div key={idx} className="skeleton-card p-3">
                    <SkeletonBlock width="40%" height={18} className="mb-3" />
                    <SkeletonText
                      lines={3}
                      lineHeight={12}
                      gap={8}
                      widths={["100%", "96%", "80%"]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {showSidebar && (
          <div className="col-12 col-xl-4" style={{ maxWidth: "400px", flex: "0 1 400px" }}>
            <div className="skeleton-card mb-4">
              <SkeletonBlock width="60%" height={18} className="mb-3" />
              <SkeletonBadgeRow count={3} width="70px" />
              <SkeletonText
                lines={3}
                lineHeight={12}
                gap={8}
                widths={["100%", "90%", "60%"]}
                className="mt-3"
              />
            </div>
            <div className="skeleton-card">
              <SkeletonBlock width="50%" height={18} className="mb-3" />
              <SkeletonText
                lines={4}
                lineHeight={12}
                gap={8}
                widths={["100%", "96%", "90%", "70%"]}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const RelatedNewsSkeleton = () => (
  <div className="row g-4" aria-hidden="true">
    {Array.from({ length: 4 }).map((_, idx) => (
      <div key={idx} className="col-md-6 col">
        <NewsCardSkeleton viewMode="grid" />
      </div>
    ))}
  </div>
);

const NewsSkeletons = {
  TopNewsSkeleton,
  NewsCardSkeleton,
  HotNewsSkeleton,
  AllNewsSkeleton,
  ArticleSkeleton,
  RelatedNewsSkeleton,
};

export default NewsSkeletons;
